import { useQuery } from '@apollo/client';
import { useRouter } from '@application/utils';
import { graphql } from '@gql/gql';
import type { SelectOption } from '@presentation/components';
import { partition, sortBy } from 'lodash/fp';
import { useCallback, useMemo } from 'react';

export const GET_BREEDS = graphql(`
  query GetBreeds($where: DogBreedWhereInput, $translationsWhere: DogBreedTranslationWhereInput) {
    findManyDogBreed(where: $where) {
      id
      name
      sizeCategory
      baseBreedName
      isSubBreed
      translations(where: $translationsWhere) {
        id
        name
        otherNames
      }
    }
  }
`);

export const useGetBreeds = (options?: { skip: boolean }) => {
  const { skip = false } = options ?? {};
  const { language } = useRouter();

  const variables = useMemo(() => {
    return {
      translationsWhere: { language: { equals: language } },
    };
  }, [language]);

  const { data, loading, error } = useQuery(GET_BREEDS, {
    variables,
    skip,
    fetchPolicy: 'cache-first',
  });

  const breedOptions: SelectOption<number>[] = useMemo(() => {
    const sortedByName = sortBy(
      (b) => b.translations?.[0]?.name?.toLocaleLowerCase(),
      data?.findManyDogBreed ?? [],
    );

    return sortedByName.map(
      (breed) =>
        ({
          value: breed.id,
          label: breed.translations[0].name,
          searchValues: breed.translations[0].otherNames,
        } as SelectOption<number>),
    );
  }, [data?.findManyDogBreed]);

  const breedOptionsWithCrossbreedsFirst: SelectOption<number>[] = useMemo(() => {
    const sortedByName = sortBy(
      (b) => b.translations?.[0]?.name?.toLocaleLowerCase(),
      data?.findManyDogBreed ?? [],
    );
    const partitionned = partition(
      (breed) => ['crosssmall', 'crossmedium', 'crossbig'].includes(breed.name), // TODO: Add backend enum and use it here
      sortedByName,
    );

    partitionned[1].unshift(...partitionned[0].reverse());
    const prioritized = partitionned[1];

    return prioritized.map(
      (breed) =>
        ({
          value: breed.id,
          label: breed.translations[0].name,
          searchValues: breed.translations[0].otherNames,
        } as SelectOption<number>),
    );
  }, [data?.findManyDogBreed]);

  const breedOptionsWithoutCrossbreeds: SelectOption<number>[] = useMemo(() => {
    const sortedByName = sortBy(
      (b) => b.translations?.[0]?.name?.toLocaleLowerCase(),
      data?.findManyDogBreed ?? [],
    );

    const filtered = sortedByName.filter(
      (item) => !['crosssmall', 'crossmedium', 'crossbig'].includes(item.name),
    );

    return filtered.map(
      (breed) =>
        ({
          value: breed.id,
          label: breed.translations[0].name,
          searchValues: breed.translations[0].otherNames,
        } as SelectOption<number>),
    );
  }, [data?.findManyDogBreed]);

  const breedOptionsBasenameKey: SelectOption<string>[] = useMemo(() => {
    return sortBy(
      (b) => b.translations?.[0]?.name?.toLocaleLowerCase(),
      data?.findManyDogBreed ?? [],
    ).map(
      (breed) =>
        ({
          value: breed.baseBreedName ?? breed.name,
          label: breed.translations[0].name,
          searchValues: breed.translations[0].otherNames,
        } as SelectOption<string>),
    );
  }, [data?.findManyDogBreed]);

  const getParentBreedName = useCallback(
    (breedName: string) => {
      const foundSubBreed = data?.findManyDogBreed?.find(
        (x) => x.isSubBreed && x.name === breedName,
      );
      if (foundSubBreed?.isSubBreed) {
        return foundSubBreed.baseBreedName;
      }
      return breedName;
    },
    [data?.findManyDogBreed],
  );

  return {
    breedsLoading: loading,
    breedsError: error,
    breeds: data?.findManyDogBreed ?? [],
    breedOptions,
    breedOptionsWithCrossbreedsFirst,
    breedOptionsWithoutCrossbreeds,
    breedOptionsBasenameKey,
    getParentBreedName,
  };
};
